import { TAction } from '../../../actions/types/action';
import { TSaveSelfReportScoresPayload } from '../../../services/commonApiService';
import {
  TDynamicEventBracketStateItem,
  TEventBracketDetailsState,
  TEventBracketListState,
  TEventDetailsState,
  TEventRegistrationDetailsState,
  TEventRegistrationFormState,
  TEventRegistrationStepFormItem,
  TEventReminderListState,
  TEventTeamListState,
  TSaveEventUserRegistrationPayload
} from './types';
import { IEventRegistrationUserStepData, TMultiRegistrationEventFormItem } from '../../../../../types/eventTypes';

const getInitialState = (): TEventDetailsState => ({
  eventRegistrationDetails: {
    isLoading: false,
    isLoaded: false,
    details: null,
    ui: {}
  },
  eventTeamList: {
    isLoading: false,
    isLoaded: false,
    list: [],
    ui: {
      count: 0,
      page: 0,
      pageSize: 20,
      minTeamSize: null,
      maxTeamSize: null,
      search: ''
    }
  },
  eventReminderList: {
    isLoading: false,
    isRefreshing: false,
    isLoaded: false,
    list: [],
    ui: {}
  },
  eventRegistrationForm: null,
  requestPending: false,

  eventBracketList: {
    list: [],
    isLoaded: false,
    isLoading: false,
    ui: {},
    relatedEvents: []
  },
  selectedEventBracketDetails: {
    isLoading: false,
    isLoaded: false,
    details: null,
    ui: {},
    isRefreshing: false
  },
  dynamic: {},
  isCheckingInRequestPending: false
});

// Action Types
const VIEW_EVENT_USER_REGISTRATION = 'EVENT_USER_REGISTRATION/VIEW_EVENT_USER_REGISTRATION';
const VIEW_EVENT_TEAM_LIST = 'EVENT_USER_REGISTRATION/VIEW_EVENT_TEAM_LIST';
const VIEW_EVENT_REMINDER_LIST = 'EVENT_USER_REGISTRATION/VIEW_EVENT_REMINDER_LIST';
const REFRESH_EVENT_REMINDER_LIST = 'EVENT_USER_REGISTRATION/REFRESH_EVENT_REMINDER_LIST';
const SAVE_EVENT_USER_REGISTRATION = 'EVENT_USER_REGISTRATION/SAVE_EVENT_USER_REGISTRATION';
const CANCEL_EVENT_USER_REGISTRATION = 'EVENT_USER_REGISTRATION/CANCEL_EVENT_USER_REGISTRATION';
const CANCEL_EVENT_ENTRIES_TEAM_REGISTRATION = 'EVENT_USER_REGISTRATION/CANCEL_EVENT_ENTRIES_TEAM_REGISTRATION';

const REFRESH_BATTLE_ACCOUNT = 'EVENT_USER_REGISTRATION/REFRESH_BATTLE_ACCOUNT';
const REFRESH_CLAIMED_CHARACTERS = 'EVENT_USER_REGISTRATION/REFRESH_CLAIMED_CHARACTERS';

const JOIN_TO_EVENT_TEAM = 'EVENT_USER_REGISTRATION/JOIN_TO_EVENT_TEAM';
const CREATE_EVENT_TEAM = 'EVENT_USER_REGISTRATION/CREATE_EVENT_TEAM';
const DISBAND_EVENT_TEAM = 'EVENT_USER_REGISTRATION/DISBAND_EVENT_TEAM';
const LEAVE_FROM_EVENT_TEAM = 'EVENT_USER_REGISTRATION/LEAVE_FROM_EVENT_TEAM';
const REGENERATE_EVENT_TEAM_INVITE = 'EVENT_USER_REGISTRATION/REGENERATE_EVENT_TEAM_INVITE';
const SAVE_SELF_REPORT_SCORES = 'EVENT_USER_REGISTRATION/SAVE_SELF_REPORT_SCORES';
const CHECK_IN_EVENT_BRACKET_TEAM = 'EVENT_USER_REGISTRATION/CHECK_IN_EVENT_BRACKET_TEAM';

const SET_REQUEST_PENDING = 'EVENTS_MANAGEMENT/SET_REQUEST_PENDING';
const SET_EVENT_USER_REGISTRATION_FORM = 'EVENTS_MANAGEMENT/SET_EVENT_USER_REGISTRATION_FORM';
const UPDATE_REGISTRATION_STEP = 'EVENTS_MANAGEMENT/UPDATE_REGISTRATION_STEP';

const UPDATE_MULTI_REGISTRATION_EVENTS = 'EVENTS_MANAGEMENT/UPDATE_MULTI_REGISTRATION_EVENTS';

const LOAD_EVENT_USER_REGISTRATION = 'EVENT_USER_REGISTRATION/LOAD_EVENT_USER_REGISTRATION';
const LOAD_EVENT_TEAM_LIST = 'EVENT_USER_REGISTRATION/LOAD_EVENT_TEAM_LIST';
const LOAD_EVENT_REMINDER_LIST = 'EVENT_USER_REGISTRATION/LOAD_EVENT_REMINDER_LIST';

const LOAD_EVENT_BRACKET_LIST = 'EVENT_USER_REGISTRATION/LOAD_EVENT_BRACKET_LIST';
const VIEW_EVENT_BRACKET_LIST = 'EVENT_USER_REGISTRATION/VIEW_EVENT_BRACKET_LIST';

const LOAD_EVENT_BRACKET_DETAILS = 'EVENT_USER_REGISTRATION/LOAD_EVENT_BRACKET_DETAILS';
const VIEW_EVENT_BRACKET_DETAILS = 'EVENT_USER_REGISTRATION/VIEW_EVENT_BRACKET_DETAILS';
const REFRESH_EVENT_BRACKET_DETAILS = 'EVENT_USER_REGISTRATION/REFRESH_EVENT_BRACKET_DETAILS';

const LOAD_DYNAMIC_BRACKET = 'EVENT_USER_REGISTRATION/LOAD_DYNAMIC_BRACKET';
const SET_DYNAMIC_BRACKET = 'EVENT_USER_REGISTRATION/SET_DYNAMIC_BRACKET';

export const REMOVE_REQUIRED_REGISTRATION_EVENT = 'REMOVE_REQUIRED_REGISTRATION_EVENT';
const SET_IS_CHECKING_IN_REQUEST_PENDING = 'SET_IS_CHECKING_IN_REQUEST_PENDING';

// Actions
const viewEventUserRegistration = (slug: string, inviteCode?: string) => {
  return {
    type: VIEW_EVENT_USER_REGISTRATION,
    payload: {
      slug,
      inviteCode
    }
  };
};

const viewEventTeamList = (eventSlug: string, page: number, pageSize: number, search: string) => {
  return {
    type: VIEW_EVENT_TEAM_LIST,
    payload: {
      eventSlug,
      page,
      pageSize,
      search
    }
  };
};

const viewEventReminderList = (eventSlug: string) => {
  return {
    type: VIEW_EVENT_REMINDER_LIST,
    payload: {
      eventSlug
    }
  };
};

const refreshEventReminderList = (eventSlug: string) => {
  return {
    type: REFRESH_EVENT_REMINDER_LIST,
    payload: {
      eventSlug
    }
  };
};

const saveEventUserRegistration = (
  slug: string,
  data: { steps: IEventRegistrationUserStepData[]; multiRegistrationEvents: TMultiRegistrationEventFormItem[] },
  inviteCode?: string
): TAction<TSaveEventUserRegistrationPayload> => {
  return {
    type: SAVE_EVENT_USER_REGISTRATION,
    payload: {
      slug,
      data,
      inviteCode
    }
  };
};

const cancelEventUserRegistration = (slug: string) => {
  return {
    type: CANCEL_EVENT_USER_REGISTRATION,
    payload: {
      slug
    }
  };
};

const cancelEventEntriesTeamRegistration = (slug: string, teamId: number) => {
  return {
    type: CANCEL_EVENT_ENTRIES_TEAM_REGISTRATION,
    payload: {
      slug,
      teamId
    }
  };
};

const joinToEventTeam = (eventRegistrationStepId: number, charterId: number) => {
  return {
    type: JOIN_TO_EVENT_TEAM,
    payload: {
      eventRegistrationStepId,
      charterId
    }
  };
};

const createEventTeam = (eventRegistrationStepId: number, values: any) => {
  return {
    type: CREATE_EVENT_TEAM,
    payload: {
      eventRegistrationStepId,
      values
    }
  };
};

const disbandEventTeam = (charterId: number, eventRegistrationStepId: number) => {
  return {
    type: DISBAND_EVENT_TEAM,
    payload: {
      charterId,
      eventRegistrationStepId
    }
  };
};

const leaveFromEventTeam = (charterId: number, eventRegistrationStepId: number) => {
  return {
    type: LEAVE_FROM_EVENT_TEAM,
    payload: {
      charterId,
      eventRegistrationStepId
    }
  };
};

const regenerateEventTeamInvite = (charterId: number, eventRegistrationStepId?: number, eventId?: number) => {
  return {
    type: REGENERATE_EVENT_TEAM_INVITE,
    payload: {
      charterId,
      eventRegistrationStepId,
      eventId
    }
  };
};

const saveSelfReportScores = (eventSlug: string, bracketSlug: string, payload: TSaveSelfReportScoresPayload) => {
  return {
    type: SAVE_SELF_REPORT_SCORES,
    payload: {
      eventSlug,
      bracketSlug,
      payload
    }
  };
};

const checkInEventBracketTeam = (eventSlug: string, bracketSlug: string, bracketTeamId: number) => {
  return {
    type: CHECK_IN_EVENT_BRACKET_TEAM,
    payload: {
      eventSlug,
      bracketSlug,
      bracketTeamId
    }
  };
};

const setRequestPending = (payload: boolean): TAction<Partial<TEventDetailsState>> => {
  return {
    type: SET_REQUEST_PENDING,
    payload: {
      requestPending: payload
    }
  };
};

const setEventUserRegistrationForm = (payload: TEventRegistrationFormState): TAction<Partial<TEventDetailsState>> => {
  return {
    type: SET_EVENT_USER_REGISTRATION_FORM,
    payload: {
      eventRegistrationForm: payload
    }
  };
};

const updateRegistrationStep = (payload: TEventRegistrationStepFormItem): TAction<TEventRegistrationStepFormItem> => {
  return {
    type: UPDATE_REGISTRATION_STEP,
    payload
  };
};

const updateMultiRegistrationEvents = (
  payload: TMultiRegistrationEventFormItem[]
): TAction<TMultiRegistrationEventFormItem[]> => {
  return {
    type: UPDATE_MULTI_REGISTRATION_EVENTS,
    payload
  };
};

const loadEventUserRegistration = (payload: TEventRegistrationDetailsState): TAction<Partial<TEventDetailsState>> => {
  return {
    type: LOAD_EVENT_USER_REGISTRATION,
    payload: {
      eventRegistrationDetails: payload
    }
  };
};

const loadEventTeamList = (payload: TEventTeamListState): TAction<Partial<TEventDetailsState>> => {
  return {
    type: LOAD_EVENT_TEAM_LIST,
    payload: {
      eventTeamList: payload
    }
  };
};

const loadEventReminderList = (payload: TEventReminderListState): TAction<Partial<TEventDetailsState>> => {
  return {
    type: LOAD_EVENT_REMINDER_LIST,
    payload: {
      eventReminderList: payload
    }
  };
};

const loadEventBracketList = (payload: TEventBracketListState): TAction<Partial<TEventDetailsState>> => {
  return {
    type: LOAD_EVENT_BRACKET_LIST,
    payload: {
      eventBracketList: payload
    }
  };
};

const loadEventBracketDetails = (payload: TEventBracketDetailsState): TAction<Partial<TEventDetailsState>> => {
  return {
    type: LOAD_EVENT_BRACKET_DETAILS,
    payload: {
      selectedEventBracketDetails: payload
    }
  };
};

const refreshEventBracketDetails = (eventSlug: string, bracketSlug: string) => {
  return {
    type: REFRESH_EVENT_BRACKET_DETAILS,
    payload: {
      eventSlug,
      bracketSlug
    }
  };
};

const viewEventBracketList = (eventSlug: string) => {
  return {
    type: VIEW_EVENT_BRACKET_LIST,
    payload: {
      eventSlug
    }
  };
};

const viewEventBracketDetails = (eventSlug: string, bracketSlug: string) => {
  return {
    type: VIEW_EVENT_BRACKET_DETAILS,
    payload: {
      eventSlug,
      bracketSlug
    }
  };
};

const refreshBattleAccount = () => {
  return {
    type: REFRESH_BATTLE_ACCOUNT,
    payload: {}
  };
};

const refreshClaimedCharacters = () => {
  return {
    type: REFRESH_CLAIMED_CHARACTERS,
    payload: {}
  };
};

const loadDynamicBracket = (eventSlug: string, bracketSlug: string) => {
  return {
    type: LOAD_DYNAMIC_BRACKET,
    payload: {
      eventSlug,
      bracketSlug
    }
  };
};

const setDynamicBracket = (eventSlug: string, state: TDynamicEventBracketStateItem) => {
  return {
    type: SET_DYNAMIC_BRACKET,
    payload: {
      eventSlug,
      state
    }
  };
};

const removeRequiredRegistrationEvent = (eventId: number): TAction<{ eventId: number }> => {
  return {
    type: REMOVE_REQUIRED_REGISTRATION_EVENT,
    payload: {
      eventId
    }
  };
};

const setIsCheckingInRequestPending = (payload: boolean): TAction<Partial<TEventDetailsState>> => {
  return {
    type: SET_IS_CHECKING_IN_REQUEST_PENDING,
    payload: {
      isCheckingInRequestPending: payload
    }
  };
};

function reducer(state = getInitialState(), action: TAction<any>) {
  switch (action.type) {
    case SET_REQUEST_PENDING:
    case LOAD_EVENT_USER_REGISTRATION:
    case LOAD_EVENT_TEAM_LIST:
    case LOAD_EVENT_REMINDER_LIST:
    case LOAD_EVENT_BRACKET_LIST:
    case LOAD_EVENT_BRACKET_DETAILS:
    case SET_EVENT_USER_REGISTRATION_FORM: {
      return {
        ...state,
        ...action.payload
      };
    }
    case UPDATE_REGISTRATION_STEP: {
      return {
        ...state,
        eventRegistrationForm: {
          ...state.eventRegistrationForm,
          registrationSteps: state.eventRegistrationForm?.registrationSteps.map((registrationStep) => {
            const isMatchOrdinal = action.payload.ordinal === registrationStep.ordinal;
            const isMatchType = action.payload.type === registrationStep.type;

            if (isMatchOrdinal && isMatchType) {
              return action.payload;
            }

            return registrationStep;
          })
        }
      };
    }
    case SET_IS_CHECKING_IN_REQUEST_PENDING: {
      return {
        ...state,
        isCheckingInRequestPending: action.payload.isCheckingInRequestPending
      };
    }
    case UPDATE_MULTI_REGISTRATION_EVENTS: {
      return {
        ...state,
        eventRegistrationForm: {
          ...state.eventRegistrationForm,
          multiRegistrationEvents: action.payload
        }
      };
    }
    case SET_DYNAMIC_BRACKET: {
      return {
        ...state,
        dynamic: {
          ...state.dynamic,
          [action.payload.eventSlug]: action.payload.state
        }
      };
    }

    default:
      return state;
  }
}

const actions = {
  viewEventUserRegistration,
  viewEventTeamList,
  viewEventReminderList,
  viewEventBracketList,
  refreshEventReminderList,
  saveEventUserRegistration,
  cancelEventUserRegistration,
  cancelEventEntriesTeamRegistration,

  joinToEventTeam,
  createEventTeam,
  disbandEventTeam,
  leaveFromEventTeam,
  regenerateEventTeamInvite,
  saveSelfReportScores,
  checkInEventBracketTeam,

  setRequestPending,
  setEventUserRegistrationForm,
  updateRegistrationStep,
  updateMultiRegistrationEvents,

  loadEventUserRegistration,
  loadEventTeamList,
  loadEventReminderList,

  loadEventBracketList,
  viewEventBracketDetails,
  loadEventBracketDetails,
  refreshEventBracketDetails,

  refreshBattleAccount,
  refreshClaimedCharacters,

  loadDynamicBracket,
  setDynamicBracket,
  removeRequiredRegistrationEvent,
  setIsCheckingInRequestPending
};

const actionTypes = {
  VIEW_EVENT_USER_REGISTRATION,
  VIEW_EVENT_TEAM_LIST,
  VIEW_EVENT_REMINDER_LIST,
  REFRESH_EVENT_REMINDER_LIST,
  VIEW_EVENT_BRACKET_LIST,
  VIEW_EVENT_BRACKET_DETAILS,
  REFRESH_EVENT_BRACKET_DETAILS,
  SAVE_EVENT_USER_REGISTRATION,
  CANCEL_EVENT_USER_REGISTRATION,
  CANCEL_EVENT_ENTRIES_TEAM_REGISTRATION,

  JOIN_TO_EVENT_TEAM,
  CREATE_EVENT_TEAM,
  DISBAND_EVENT_TEAM,
  LEAVE_FROM_EVENT_TEAM,
  REGENERATE_EVENT_TEAM_INVITE,
  SAVE_SELF_REPORT_SCORES,
  CHECK_IN_EVENT_BRACKET_TEAM,

  SET_REQUEST_PENDING,
  SET_EVENT_USER_REGISTRATION_FORM,

  LOAD_EVENT_USER_REGISTRATION,
  LOAD_EVENT_TEAM_LIST,
  LOAD_EVENT_REMINDER_LIST,
  LOAD_EVENT_BRACKET_LIST,

  REFRESH_BATTLE_ACCOUNT,
  REFRESH_CLAIMED_CHARACTERS,
  LOAD_DYNAMIC_BRACKET,

  UPDATE_MULTI_REGISTRATION_EVENTS
};

export { actions, actionTypes, reducer };
